import { Box } from "@mui/material";
import React, { forwardRef, useState } from "react";

import { BoundingBoxesProvider } from "context/boundingBoxesContext";
import { ImageSettingsProvider } from "context/ImageSettingsContext/imageSettingsContext";
import { Canvas, CanvasRef } from "features/canvas/Canvas";
import { ShapeType } from "features/canvas/components/ShapeRenderer";
import { Point, Size } from "features/canvas/types";
import { CanvasAnnotation } from "types/imageAnnotations";

import { AnnotatioToolSettings, AnnotatorTool, AnnotatorTools } from "./AnnotatorTools/AnnotatorTools";
import { ANNOTATION_COLORS } from "./AnnotatorTools/constants";
import { StrokeWidth } from "./AnnotatorTools/StrokeSettings/StrokeSettings";

function getShapeFromAnnotatorTool(tool: AnnotatorTool): ShapeType | null {
  if (tool === "rectangle") {
    return "rectangle";
  }

  if (tool === "line") {
    return "line";
  }

  if (tool === "circle") {
    return "circle";
  }

  if (tool === "arrow") {
    return "arrow";
  }

  if (tool === "text") {
    return "text";
  }

  return null;
}

type ImageAnnotatorProps = {
  imageUrl: string;
  annotations: CanvasAnnotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<CanvasAnnotation[]>>;
  blurAnnotations: () => void;
};

export const ImageAnnotator = forwardRef<CanvasRef, ImageAnnotatorProps>(
  ({ imageUrl, annotations, setAnnotations, blurAnnotations }, ref) => {
    const [boxContainerSize, setBoxContainerSize] = useState<Size>({
      width: 0,
      height: 0,
    });
    const [position, setPosition] = useState<Point>({
      x: 0,
      y: 0,
    });
    const [settings, setSettings] = useState<AnnotatioToolSettings>({
      tool: "selector",
      strokeColor: ANNOTATION_COLORS[0],
      strokeWidth: StrokeWidth.THIN,
    });
    const [selectedAnnotation, setSelectedAnnotation] = useState<CanvasAnnotation>();

    const deleteAnnotation = (id: string) => {
      const newAnnotations = annotations.filter((annotation) => annotation.id !== id);
      setAnnotations(newAnnotations);
    };

    const handleAnnotationClick = (id: string) => {
      if (settings.tool === "eraser") {
        deleteAnnotation(id);
      }
    };

    const canDraw = settings.tool !== "eraser" && settings.tool !== "selector";

    return (
      <Box
        width="100%"
        height="100%"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          objectFit: "contain",
          alignSelf: "center",
        }}
      >
        <AnnotatorTools
          settings={settings}
          onChange={(newSettings) => {
            const hasToolChanged = newSettings.tool !== settings.tool;

            if (hasToolChanged) {
              blurAnnotations();
            }

            setSettings(newSettings);
          }}
        />

        <BoundingBoxesProvider isBoundingBoxes={annotations.length > 0} canDraw={canDraw}>
          <ImageSettingsProvider isEditing>
            <Canvas
              ref={ref}
              imageUrl={imageUrl}
              style={{
                cursor:
                  settings.tool === "eraser"
                    ? `url(${process.env.PUBLIC_URL}/static/icons/eraser-cursor.svg) 2 17, auto`
                    : undefined,
              }}
              annotations={annotations}
              position={position}
              setPosition={setPosition}
              boxContainerSize={boxContainerSize}
              setBoxContainerSize={setBoxContainerSize}
              setAnnotations={setAnnotations}
              notifyUnsavedAnnotations={false}
              annotationShape={getShapeFromAnnotatorTool(settings.tool) ?? undefined}
              selectedAnnotation={selectedAnnotation}
              setSelectedAnnotation={setSelectedAnnotation}
              onAnnotationClick={handleAnnotationClick}
              canInteract
              enablePanZoom
              enableAnnotation
              hideAnnotationMiddleCross
              hideAnnotationDelete
              annotationSettings={{
                width: settings.strokeWidth,
                color: settings.strokeColor,
              }}
              allowSmallAnnotations
            />
          </ImageSettingsProvider>
        </BoundingBoxesProvider>
      </Box>
    );
  }
);
